import type { DeviceApi } from './api/device-api.js';
import type { Blocklist } from './blocklist.js';
import type { CacheManager } from './cache/manager.js';
import type { TimeSource } from './clock.js';
import {
  ALIVE_REPORT_INTERVAL_SECONDS,
  BOOTSTRAP_HEARTBEAT_INTERVAL_SECONDS,
  BOOTSTRAP_POLL_INTERVAL_SECONDS,
  BOOTSTRAP_EVENT_BATCH_SIZE,
  PAIRING_CODE_WATCH_MS,
} from './constants.js';
import type { EventQueue } from './events/queue.js';
import type { EventUploader } from './events/uploader.js';
import type { HeartbeatEmitter } from './heartbeat.js';
import { HOUSE_CONTENT_HOLD_MS, type HouseContentSource } from './house.js';
import type { IdFactory } from './ids.js';
import type { Logger } from './log.js';
import type { Manifest, ManifestItem, QuietHours, StoredManifest } from './manifest/types.js';
import type { CardKind, Presentation, Renderer } from './playback/presentation.js';
import {
  DEFAULT_BACKGROUND_COLOR,
  DEFAULT_SCALING_MODE,
  isOrientationMismatch,
} from './playback/scaling.js';
import { EMPTY_PLAYER_STATE, type PersistedPlayerState, type PlayerStateStore } from './state/persist.js';
import type { Cancel, Timer } from './timer.js';
import { ALWAYS_VISIBLE, type VisibilitySource } from './visibility.js';

/**
 * The player state machine (technical-design §11.4):
 *
 *   BOOT → LOAD_CACHE → PLAYING (last known-good loop, from disk, no network wait)
 *                          ├─ manifest received → RECONCILE → swap at loop boundary
 *                          ├─ asset missing ────→ DOWNLOAD → verify sha256
 *                          ├─ item error ───────→ skip + report → continue
 *                          └─ empty loop ───────→ HOUSE_CONTENT
 *
 * Two rules shape the whole file. The first frame never waits on the network
 * (invariant 7): `start()` plays from disk and only then touches the API. And a
 * manifest never applies mid-item (invariant 8): a fetched manifest sits in
 * `pending` until it is fully cached and verified, and is adopted at a loop
 * boundary. There is no code path that shows an error or a black frame (invariant 9).
 */

export type PlayerState =
  | 'boot'
  | 'load_cache'
  | 'playing'
  | 'reconcile'
  | 'download'
  | 'house_content'
  | 'not_activated'
  | 'stopped';

export type SkipReason =
  | 'blocklisted'
  | 'expired'
  | 'asset_missing'
  | 'sha256_mismatch'
  | 'quiet_hours';

export type PlayerEvent =
  | { readonly type: 'state'; readonly state: PlayerState }
  | { readonly type: 'item_started'; readonly item_id: string; readonly manifest_version: number }
  | { readonly type: 'item_finished'; readonly item_id: string; readonly completed: boolean }
  | { readonly type: 'item_skipped'; readonly item_id: string; readonly reason: SkipReason }
  | { readonly type: 'manifest_pending'; readonly manifest_version: number; readonly ready: boolean }
  | { readonly type: 'manifest_adopted'; readonly manifest_version: number }
  | { readonly type: 'house_content' }
  | { readonly type: 'card'; readonly card: CardKind }
  | { readonly type: 'loop_boundary'; readonly manifest_version: number | null }
  /**
   * dwf-063: the screen was taken away from us (`hidden: true`) or handed back.
   * Emitted on the transition only, so a listener counting these is counting windows.
   */
  | { readonly type: 'visibility'; readonly hidden: boolean };

/**
 * decision-log O126 is open: the manifest carries `quiet_hours` and no source says
 * what the player does with it. The behaviour is a product decision, so it is a
 * pluggable policy whose default is the only option that cannot break invariant 9.
 */
export type QuietHoursPolicy = 'play_normally' | 'house_content' | 'muted';

export interface PlayerDeps {
  readonly api: DeviceApi;
  readonly cache: CacheManager;
  readonly renderer: Renderer;
  readonly queue: EventQueue;
  readonly uploader: EventUploader;
  readonly heartbeat: HeartbeatEmitter;
  readonly blocklist: Blocklist;
  readonly house: HouseContentSource;
  readonly stateStore: PlayerStateStore;
  readonly time: TimeSource;
  readonly timer: Timer;
  readonly logger: Logger;
  readonly newId: IdFactory;
  /** Read over the shell's JS bridge, never from localStorage (§11.1). */
  readonly deviceToken: () => string | null;
  /** Rendered on the pairing card when the device has no token yet (§11.1). */
  readonly pairingCode?: () => string | null;
  /** D251: the QR shown beside the pairing code, when the shell has one. */
  readonly pairingQr?: () => string | null;
  /**
   * D254: have the shell forget this TV's connection, so it asks for a new code and can be
   * connected again. Absent without a shell, or on one older than bridge version 8, and then a
   * revoked TV keeps the not-activated card as it always did.
   */
  readonly forgetDevice?: () => void;
  /**
   * dwf-063: whether this screen can be seen. Defaults to `ALWAYS_VISIBLE`, which is the
   * only safe default — a host that cannot answer must not be able to park the loop.
   */
  readonly visibility?: VisibilitySource;
  readonly quietHoursPolicy?: QuietHoursPolicy;
  /**
   * Tell the shell the loop is alive. Optional: a browser tab has no shell and nothing to
   * reassure. Passed as a closure so the bundle keeps knowing nothing about Android (DF9).
   */
  readonly reportAlive?: () => void;
}

export interface Player {
  /** Boots, plays the cached loop, then starts background sync. Resolves once a
   * presentation is on screen. */
  start(): Promise<void>;
  stop(): Promise<void>;
  state(): PlayerState;
  activeManifestVersion(): number | null;
  pendingManifestVersion(): number | null;
  loopIndex(): number;
  /** `settings.event_batch_size` from the active manifest, or the §11.6 default. */
  eventBatchSize(): number;
  /** Manifest poll; also what an MQTT nudge calls (technical-design §11.3). */
  syncManifest(): Promise<ManifestSyncOutcome>;
  /** Fleet blocklist update, e.g. from `dw/fleet/blocklist` (design-addendum §2). */
  applyFleetBlocklist(entries: readonly string[]): void;
  /**
   * Stops playback and shows the not-activated card (design-addendum §11, D81), then, with a
   * shell that can forget the token, asks for a new code (D254).
   */
  markTokenRevoked(): void;
  observe(listener: (event: PlayerEvent) => void): Cancel;
}

export type ManifestSyncOutcome =
  | 'unchanged'
  | 'pending'
  | 'pending_incomplete'
  | 'invalid'
  | 'revoked'
  | 'client_error'
  | 'offline'
  | 'skipped_no_token';

export function createPlayer(deps: PlayerDeps): Player {
  const listeners = new Set<(event: PlayerEvent) => void>();
  const emit = (event: PlayerEvent): void => {
    for (const listener of listeners) listener(event);
  };

  let state: PlayerState = 'boot';
  const setState = (next: PlayerState): void => {
    if (state === next) return;
    state = next;
    deps.logger.info('player.state', { state: next });
    emit({ type: 'state', state: next });
  };

  let persisted: PersistedPlayerState = EMPTY_PLAYER_STATE;
  let active: StoredManifest | null = null;
  let pending: StoredManifest | null = null;
  let pendingReady = false;
  let revoked = false;
  let stopped = false;
  let loopIndex = 0;
  let cancelPoll: Cancel | null = null;
  let cancelHeartbeat: Cancel | null = null;
  let pollIntervalSeconds = BOOTSTRAP_POLL_INTERVAL_SECONDS;
  let heartbeatIntervalSeconds = BOOTSTRAP_HEARTBEAT_INTERVAL_SECONDS;
  let cancelAliveReport: Cancel | null = null;
  let loopTask: Promise<void> | null = null;
  let firstPresentation: (() => void) | null = null;

  /**
   * dwf-063 — the visibility rule, and how it squares with invariant 8.
   *
   * Invariant 8 ("the player never swaps mid-item") is about *manifest adoption*: a
   * fetched manifest waits in `pending` until a loop boundary. Losing the screen is not a
   * swap, it is a **stop**, and the machinery for a stop already exists — `Renderer.stop()`
   * resolves the item in flight with `{ completed: false, reason: 'stopped' }`, and this
   * file has always declined to record a play for that outcome.
   *
   * So the rule is: **a hidden window discards the item in flight rather than billing a
   * partial**, does not advance `loopIndex`, and on the way back replays that same item
   * whole from where the loop already is. No swap occurs, so invariant 8 is untouched, and
   * a manifest that became `pending` while we were hidden still waits for the next real
   * loop boundary below.
   *
   * The alternative — freeze the element and resume it where it stopped — is rejected. A
   * `<video>` resumed after a HOME press carries no guarantee about frame position, and the
   * resulting event's `started_at_device_ms`…`ended_at_device_ms` span would then contain
   * the hidden interval, which is precisely the inflation this exists to remove.
   */
  const visibility = deps.visibility ?? ALWAYS_VISIBLE;
  let hidden = visibility.hidden();
  let cancelVisibility: Cancel | null = null;
  let visibleWaiters: (() => void)[] = [];

  const releaseVisibleWaiters = (): void => {
    const waiting = visibleWaiters;
    visibleWaiters = [];
    for (const resolve of waiting) resolve();
  };

  const awaitVisible = (): Promise<void> =>
    new Promise<void>((resolve) => {
      visibleWaiters.push(resolve);
    });

  const onVisibilityChanged = (nextHidden: boolean): void => {
    if (nextHidden === hidden || stopped) return;
    hidden = nextHidden;
    emit({ type: 'visibility', hidden });
    if (hidden) {
      deps.logger.info('player.hidden', { loop_index: loopIndex });
      // Discard the item in flight. `stop()` on the renderer resolves it as
      // `reason: 'stopped'`, which `playItem` answers with no play event and no advance.
      deps.renderer.stop();
      return;
    }
    deps.logger.info('player.visible', { loop_index: loopIndex });
    releaseVisibleWaiters();
  };

  const quietHoursPolicy = deps.quietHoursPolicy ?? 'play_normally';

  const savePersisted = async (): Promise<void> => {
    persisted = {
      active,
      pending,
      loop_index: loopIndex,
      clock_offset_ms: deps.time.offsetMs(),
      boot_count: persisted.boot_count,
      last_error: deps.logger.lastError()?.event ?? null,
      updated_at_device_ms: deps.time.deviceNowMs(),
    };
    try {
      await deps.stateStore.save(persisted);
    } catch (error) {
      deps.logger.warn('state.save_failed', {
        reason: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const repin = (): void => {
    const digests = new Set<string>();
    for (const item of active?.manifest.loop ?? []) digests.add(item.asset_sha256);
    for (const item of pending?.manifest.loop ?? []) digests.add(item.asset_sha256);
    deps.cache.pin(digests);
  };

  const settings = (): Manifest['settings'] | null => active?.manifest.settings ?? null;

  const batchSize = (): number => settings()?.event_batch_size ?? BOOTSTRAP_EVENT_BATCH_SIZE;

  const schedulePolling = (): void => {
    const seconds = active?.manifest.poll_interval_seconds ?? BOOTSTRAP_POLL_INTERVAL_SECONDS;
    if (cancelPoll !== null && seconds === pollIntervalSeconds) return;
    cancelPoll?.();
    pollIntervalSeconds = seconds;
    cancelPoll = deps.timer.every(seconds * 1000, () => {
      void syncManifest();
    });
  };

  const scheduleAliveReport = (): void => {
    // Idempotent, and a fixed interval, so unlike the other two there is nothing to compare.
    if (cancelAliveReport !== null || deps.reportAlive === undefined) return;
    const report = deps.reportAlive;
    report();
    cancelAliveReport = deps.timer.every(ALIVE_REPORT_INTERVAL_SECONDS * 1000, () => {
      report();
    });
  };

  const scheduleHeartbeat = (): void => {
    const seconds =
      settings()?.heartbeat_interval_seconds ?? BOOTSTRAP_HEARTBEAT_INTERVAL_SECONDS;
    // Idempotent, exactly as `schedulePolling` above already is. This one was not: it cancelled
    // and restarted its timer on every call, and it is called on every `manifest.adopted`, which
    // happens on the *poll* interval. With the shipped 60-second poll and 300-second heartbeat a
    // restarted timer would never reach zero.
    //
    // Worth being exact about what this does and does not fix. It was the obvious suspect for a
    // bench stick that played for 25 minutes and never sent a heartbeat, and it is a real defect
    // on its own terms — the twin scheduler above has this guard and this one did not. It was
    // *not* the cause: with the guard in place and the new bundle provably running, a re-paired
    // stick still sent no heartbeat in seven minutes. The cause is somewhere in `beat()`, and
    // the `catch` below is there to say so out loud the next time it happens.
    if (cancelHeartbeat !== null && seconds === heartbeatIntervalSeconds) return;
    cancelHeartbeat?.();
    heartbeatIntervalSeconds = seconds;
    cancelHeartbeat = deps.timer.every(seconds * 1000, () => {
      void (async () => {
        // Entry, before anything can hang. Without this there is no way to tell a timer that
        // never fires from a `beat()` that never resolves, and those have opposite fixes.
        deps.logger.info('heartbeat.tick', { interval_seconds: seconds });
        // `beat()` awaits `build()`, which reads the cache size and the queued-event count. A
        // throw in either used to land in this `void` and vanish: no POST, no log, and a fleet
        // dashboard quietly reporting a playing screen as dark. Whatever goes wrong here is
        // worth one line in the console.
        try {
          const outcome = await deps.heartbeat.beat();
          if (outcome === 'revoked') markTokenRevoked();
          await deps.uploader.flush();
        } catch (error) {
          deps.logger.error('heartbeat.failed', {
            reason: error instanceof Error ? error.message : String(error),
          });
        }
      })();
    });
  };

  const markTokenRevoked = (): void => {
    if (revoked) return;
    revoked = true;
    deps.logger.error('device.token_revoked', {});
    deps.renderer.stop();
    emit({ type: 'card', card: 'not_activated' });
    const forget = deps.forgetDevice;
    if (forget !== undefined) void forgetRevokedDevice(forget);
  };

  /**
   * D254: a TV whose token is refused asks to be connected again. It used to hold the
   * not-activated card until someone cleared its app data with the remote, which nobody at a
   * venue knows to do, so a TV retired on a swap or after 30 days dark was lost hardware.
   *
   * Three things, in this order. Upload what it can first: a retired token may still post the
   * plays it holds for the screen they played on (O113), and this is the token's last use.
   * Whatever is refused stays queued and goes up after the TV is connected again, under the
   * screen each event names. Then drop the old screen's loop, because the TV may be connected
   * somewhere else next, and playing that loop there would record another venue's ads as played
   * at the old one. Only then forget the token, which makes the shell ask for a new code.
   */
  const forgetRevokedDevice = async (forget: () => void): Promise<void> => {
    try {
      const flushed = await deps.uploader.flush();
      deps.logger.info('device.revoked_flush', { uploaded: flushed.uploaded, outcome: flushed.outcome });
    } catch (error) {
      deps.logger.warn('device.revoked_flush_failed', {
        reason: error instanceof Error ? error.message : String(error),
      });
    }
    active = null;
    pending = null;
    pendingReady = false;
    loopIndex = 0;
    repin();
    await savePersisted();
    forget();
    revoked = false;
    deps.logger.info('device.forgotten', { queued_events: await deps.queue.size() });
    // Ends the not-activated card's hold, so the next pass shows the pairing card at once.
    deps.renderer.stop();
  };

  const adoptPendingIfReady = async (): Promise<void> => {
    if (pending === null || !pendingReady) return;
    active = pending;
    pending = null;
    pendingReady = false;
    loopIndex = 0;
    repin();
    deps.cache.setCeiling(active.manifest.settings.cache_max_bytes);
    deps.blocklist.updateFromManifest(active.manifest.settings.blocklist);
    await savePersisted();
    schedulePolling();
    scheduleHeartbeat();
    deps.logger.info('manifest.adopted', {
      manifest_version: active.manifest.manifest_version,
      loop_items: active.manifest.loop.length,
    });
    emit({ type: 'manifest_adopted', manifest_version: active.manifest.manifest_version });
    setState('playing');
  };

  const presentCard = async (card: CardKind): Promise<void> => {
    const code = card === 'pairing' ? (deps.pairingCode?.() ?? null) : null;
    // No code, no QR: a QR for a code the card is not showing would pair to nothing.
    const qr = code === null ? null : (deps.pairingQr?.() ?? null);
    const presentation: Presentation = {
      kind: 'card',
      card,
      pairing_code: code,
      pairing_qr_svg: qr,
      hold_ms: HOUSE_CONTENT_HOLD_MS,
    };
    emit({ type: 'card', card });
    // Redraw the moment the shell's code changes rather than at the end of the hold: from none
    // to its first, or from an expired one to its replacement (PAIRING_CODE_WATCH_MS).
    const readCode = card === 'pairing' ? deps.pairingCode : undefined;
    const cancelWatch =
      readCode === undefined
        ? null
        : deps.timer.every(PAIRING_CODE_WATCH_MS, () => {
            if ((readCode() ?? null) !== code) deps.renderer.stop();
          });
    try {
      await presentAndSignal(presentation);
    } finally {
      cancelWatch?.();
    }
  };

  const presentHouse = async (): Promise<void> => {
    setState('house_content');
    const asset = await deps.house.current();
    emit({ type: 'house_content' });
    await presentAndSignal({ kind: 'house', asset, duration_ms: HOUSE_CONTENT_HOLD_MS });
  };

  const isExpired = (item: ManifestItem): boolean => {
    if (item.ends_at === null) return false;
    const endsAt = Date.parse(item.ends_at);
    if (Number.isNaN(endsAt)) return false;
    // An implausible, uncorrected clock must not expire live inventory.
    if (deps.time.isUnsynced()) return false;
    return deps.time.nowMs() > endsAt;
  };

  /**
   * Plays one item. Returns whether the loop should move on: `false` means the item was
   * interrupted (visibility lost, revocation, shutdown) and must be replayed whole from
   * the same `loopIndex` rather than billed as a partial and skipped past.
   */
  const playItem = async (
    item: ManifestItem,
    manifestVersion: number,
    screenId: string,
  ): Promise<boolean> => {
    const read = await deps.cache.readForPlay(item);
    if (!read.ok) {
      // Skip, re-download once in the background, report; never stall (§11.7).
      setState('download');
      deps.logger.error('item.asset_unavailable', {
        item_id: item.item_id,
        reason: read.reason,
      });
      emit({ type: 'item_skipped', item_id: item.item_id, reason: read.reason === 'missing' ? 'asset_missing' : 'sha256_mismatch' });
      deps.cache.prefetch(item);
      return true;
    }

    const muted = item.audio_enabled !== true;
    const presentation: Presentation = {
      kind: 'asset',
      item,
      asset: read.asset,
      scaling: item.scaling_mode ?? DEFAULT_SCALING_MODE,
      background_color: item.background_color ?? DEFAULT_BACKGROUND_COLOR,
      muted: quietHoursPolicy === 'muted' ? true : muted,
      duration_ms: item.duration_ms,
    };

    setState('playing');
    emit({ type: 'item_started', item_id: item.item_id, manifest_version: manifestVersion });

    const startedAtDeviceMs = deps.time.deviceNowMs();
    const outcome = await presentAndSignal(presentation);
    const endedAtDeviceMs = deps.time.deviceNowMs();

    if (outcome.source_width !== undefined && outcome.source_height !== undefined) {
      const source = { width: outcome.source_width, height: outcome.source_height };
      if (isOrientationMismatch(source, deps.renderer.viewport())) {
        // design-addendum §5: pillarboxed and logged, never dropped.
        deps.logger.warn('item.orientation_mismatch', {
          item_id: item.item_id,
          source_width: source.width,
          source_height: source.height,
        });
      }
    }

    if (outcome.reason === 'stopped') {
      // dwf-063: no play event, not even a `completed: false` one — the span would
      // contain the interval nobody saw. The loop stays where it is so the item is
      // replayed whole rather than skipped.
      deps.logger.info('item.discarded_interrupted', {
        item_id: item.item_id,
        loop_index: loopIndex,
        hidden,
      });
      return false;
    }

    await deps.queue.enqueue({
      kind: 'play',
      id: deps.newId(),
      screen_id: screenId,
      item_id: item.item_id,
      campaign_id: item.campaign_id,
      creative_id: item.creative_id,
      venue_id: item.venue_id,
      started_at_device_ms: startedAtDeviceMs,
      ended_at_device_ms: endedAtDeviceMs,
      completed: outcome.completed,
      manifest_ver: manifestVersion,
      clock_unsynced: deps.time.isUnsynced(),
    });
    emit({ type: 'item_finished', item_id: item.item_id, completed: outcome.completed });

    if (!outcome.completed) {
      deps.logger.error('item.play_failed', { item_id: item.item_id, reason: outcome.reason });
    }
    return true;
  };

  const presentAndSignal = async (
    presentation: Presentation,
  ): Promise<Awaited<ReturnType<Renderer['present']>>> => {
    const promise = deps.renderer.present(presentation);
    firstPresentation?.();
    firstPresentation = null;
    return promise;
  };

  const runLoop = async (): Promise<void> => {
    while (!stopped) {
      if (hidden) {
        // Booting straight into a backgrounded WebView would otherwise hang `start()`
        // for ever on its first-frame await, and with it the heartbeat that is the only
        // way anyone would find out. Invariant 7 is about not waiting on the *network*;
        // releasing it here costs nothing, because there is no screen to paint.
        firstPresentation?.();
        firstPresentation = null;
        await awaitVisible();
        continue;
      }

      if (revoked || deps.deviceToken() === null) {
        setState('not_activated');
        await presentCard(revoked ? 'not_activated' : 'pairing');
        continue;
      }

      if (loopIndex === 0) {
        emit({ type: 'loop_boundary', manifest_version: active?.manifest.manifest_version ?? null });
        await adoptPendingIfReady();
      }

      const manifest = active?.manifest ?? null;
      const items = manifest?.loop ?? [];

      // Empty, or every item skipped: either way there is nothing to play, and the
      // screen holds house content rather than spinning through skips (§11.7).
      if (manifest === null || items.length === 0 || allItemsUnplayable(items)) {
        await presentHouse();
        continue;
      }

      if (quietHoursPolicy === 'house_content' && inQuietHours(manifest.settings.quiet_hours, deps.time.nowMs())) {
        await presentHouse();
        continue;
      }

      const index = loopIndex % items.length;
      const item = items[index];
      if (item === undefined) {
        loopIndex = 0;
        continue;
      }

      const advance = async (): Promise<void> => {
        loopIndex = (index + 1) % items.length;
        await savePersisted();
      };

      // design-addendum §2: checked before every item, skipped silently.
      if (deps.blocklist.isBlocked(item.creative_id)) {
        deps.logger.info('item.blocklisted', {
          item_id: item.item_id,
          creative_id: item.creative_id,
        });
        emit({ type: 'item_skipped', item_id: item.item_id, reason: 'blocklisted' });
        await advance();
        continue;
      }

      if (isExpired(item)) {
        // optisigns-parity §2: asset validity windows. The compiler drops expired
        // items; this is the device-side backstop when a manifest goes stale offline.
        deps.logger.info('item.expired', { item_id: item.item_id, ends_at: item.ends_at });
        emit({ type: 'item_skipped', item_id: item.item_id, reason: 'expired' });
        await advance();
        continue;
      }

      const next = items[(index + 1) % items.length];
      if (next !== undefined && next.item_id !== item.item_id) deps.cache.prefetch(next);

      if (await playItem(item, manifest.manifest_version, manifest.screen_id)) await advance();
    }
    setState('stopped');
  };

  const allItemsUnplayable = (items: readonly ManifestItem[]): boolean =>
    items.length > 0 && items.every((item) => deps.blocklist.isBlocked(item.creative_id) || isExpired(item));

  const syncManifest = async (): Promise<ManifestSyncOutcome> => {
    if (deps.deviceToken() === null) return 'skipped_no_token';

    const etag = pending?.etag ?? active?.etag ?? null;
    const result = await deps.api.fetchManifest(etag);

    switch (result.status) {
      case 'unchanged':
        return 'unchanged';

      case 'updated': {
        const stored: StoredManifest = {
          manifest: result.manifest,
          etag: result.etag,
          fetched_at_ms: deps.time.nowMs(),
        };
        pending = stored;
        pendingReady = false;
        setState('reconcile');
        repin();
        // The blocklist is a takedown path measured in seconds (design-addendum §2),
        // so it applies the moment it arrives rather than at the loop boundary.
        deps.blocklist.updateFromManifest(result.manifest.settings.blocklist);
        emit({
          type: 'manifest_pending',
          manifest_version: result.manifest.manifest_version,
          ready: false,
        });

        setState('download');
        const ensured = await deps.cache.ensureAll(result.manifest.loop);
        if (!ensured.ok) {
          // Not adopted: the last known-good loop keeps playing and its assets stay
          // pinned as the rollback target (§11.5).
          deps.logger.warn('manifest.pending_incomplete', {
            manifest_version: result.manifest.manifest_version,
            failures: ensured.failures.length,
          });
          setState(active === null ? 'house_content' : 'playing');
          return 'pending_incomplete';
        }
        pendingReady = true;
        emit({
          type: 'manifest_pending',
          manifest_version: result.manifest.manifest_version,
          ready: true,
        });
        await savePersisted();
        setState(active === null ? 'load_cache' : 'playing');
        // With nothing playing yet there is no boundary to wait for.
        if (active === null) await adoptPendingIfReady();
        return 'pending';
      }

      case 'invalid':
        deps.logger.error('manifest.invalid', { errors: result.errors.slice(0, 5) });
        return 'invalid';

      case 'revoked':
        // design-addendum §11: a revoked device shows the branded card, not the loop.
        deps.logger.error('manifest.token_revoked', {});
        markTokenRevoked();
        return 'revoked';

      case 'client_error':
        // technical-design §11.7: keep the last known-good loop, alert via heartbeat.
        deps.logger.error('manifest.client_error', { http_status: result.http_status });
        return 'client_error';

      case 'unavailable':
        deps.logger.info('manifest.unavailable', { reason: result.reason });
        return 'offline';
    }
  };

  return {
    async start() {
      setState('boot');
      try {
        persisted = (await deps.stateStore.load()) ?? EMPTY_PLAYER_STATE;
      } catch (error) {
        deps.logger.warn('state.load_failed', {
          reason: error instanceof Error ? error.message : String(error),
        });
        persisted = EMPTY_PLAYER_STATE;
      }

      setState('load_cache');
      active = persisted.active;
      pending = persisted.pending;
      loopIndex = active === null ? 0 : persisted.loop_index;
      persisted = { ...persisted, boot_count: persisted.boot_count + 1 };
      repin();
      const ceiling = active?.manifest.settings.cache_max_bytes;
      if (ceiling !== undefined) deps.cache.setCeiling(ceiling);
      deps.blocklist.updateFromManifest(active?.manifest.settings.blocklist);
      deps.logger.info('player.boot', {
        boot_count: persisted.boot_count,
        manifest_version: active?.manifest.manifest_version ?? null,
        loop_index: loopIndex,
        queued_events: await deps.queue.size(),
      });
      await savePersisted();

      // dwf-063: read the current state before subscribing, so a WebView built while the
      // shell was already in the background does not start billing on its first item.
      hidden = visibility.hidden();
      cancelVisibility = visibility.subscribe(onVisibilityChanged);

      const onScreen = new Promise<void>((resolve) => {
        firstPresentation = resolve;
      });
      loopTask = runLoop();
      await onScreen;

      // Only now does the network get a look in (invariant 7).
      schedulePolling();
      scheduleHeartbeat();
      scheduleAliveReport();
      void syncManifest().then(() => deps.uploader.flush());
    },

    async stop() {
      stopped = true;
      cancelPoll?.();
      cancelPoll = null;
      cancelHeartbeat?.();
      cancelHeartbeat = null;
      cancelAliveReport?.();
      cancelAliveReport = null;
      cancelVisibility?.();
      cancelVisibility = null;
      deps.renderer.stop();
      // A loop parked on a hidden screen is waiting on a promise nothing else will
      // resolve; without this, `stop()` never returns.
      releaseVisibleWaiters();
      await loopTask;
      await savePersisted();
      setState('stopped');
    },

    state: () => state,
    activeManifestVersion: () => active?.manifest.manifest_version ?? null,
    pendingManifestVersion: () => pending?.manifest.manifest_version ?? null,
    loopIndex: () => loopIndex,
    eventBatchSize: batchSize,
    syncManifest,
    applyFleetBlocklist: (entries) => {
      deps.blocklist.updateFromFleet(entries);
    },
    markTokenRevoked,
    observe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/** `quiet_hours` is screen-local wall clock; UTC storage is the API's side (§11.2). */
export function inQuietHours(quiet: QuietHours | null, nowMs: number): boolean {
  if (quiet === null) return false;
  const date = new Date(nowMs);
  const minutes = date.getHours() * 60 + date.getMinutes();
  const start = toMinutes(quiet.start);
  const end = toMinutes(quiet.end);
  if (start === null || end === null) return false;
  if (start === end) return false;
  return start < end ? minutes >= start && minutes < end : minutes >= start || minutes < end;
}

function toMinutes(value: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (match === null) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  return hours * 60 + minutes;
}
